import Container from '@/components/base/Container';
import Section from '@/components/base/Section';
import SectionHeading from '@/components/base/SectionHeading';
import PageIntro from '@/components/base/PageIntro';
import ServiceCard from '@/components/base/ServiceCard';
import SplitSection from '@/components/base/SplitSection';
import NumberedSteps from '@/components/base/NumberedSteps';
import ButtonLink from '@/components/base/ButtonLink';
import MaintenanceFeature from './components/MaintenanceFeature';
import ServicesClosing from './components/ServicesClosing';
import { coreServices, splitImages } from '@/mocks/site';

const processSteps = [
  { title: 'Property review', text: 'We walk the community, review existing contracts and records, and note open maintenance items and recurring resident concerns.' },
  { title: 'Management proposal', text: 'You receive a written scope of services describing what we will handle, how often we report and how requests are routed.' },
  { title: 'Transition', text: 'We collect documents, introduce ourselves to vendors and residents, and set up request intake and maintenance calendars.' },
  { title: 'Ongoing management', text: 'Daily operations begin, with regular check-ins and reports to the board or owner on the schedule agreed in the proposal.' },
  { title: 'Annual review', text: 'Once a year we review service levels, building needs and resident feedback together, and update priorities for the year ahead.' },
  { title: 'Continuous upkeep', text: 'Between reviews, inspections and preventive work continue so the property stays in the condition everyone expects.' },
];

export default function Services() {
  return (
    <>
      <PageIntro
        eyebrow="Services"
        title="Management for the whole community, not just individual units."
        lead="We handle the shared responsibilities of residential communities: operations, building upkeep, grounds, resident support and the administrative work behind them. We do not list rentals or sell homes."
        aside={<ButtonLink to="/contact?type=partnership">Request a Proposal</ButtonLink>}
      />

      <Section labelledBy="core-heading" divider>
        <Container>
          <SectionHeading
            id="core-heading"
            eyebrow="Core services"
            title="The day-to-day work of a well-run community."
            lead="Most communities rely on all four. Scope and frequency are set with each board or owner during the proposal."
          />
          <div className="grid grid-cols-1 gap-x-6 gap-y-12 md:grid-cols-2">
            {coreServices.map((s, i) => (
              <ServiceCard key={s.id} index={i + 1} title={s.title} summary={s.summary} image={s.image} tasks={s.tasks} />
            ))}
          </div>
        </Container>
      </Section>

      <SplitSection
        id="admin-heading"
        divider
        eyebrow="05 — Administrative & financial coordination"
        title="Organized records and clear budgets support better decisions."
        paragraphs={[
          'We help boards and owners keep the administrative side of the community in order: preparing draft budgets for review, tracking invoices and vendor contracts, organizing meeting materials and maintaining community records.',
          'Final financial and legal decisions remain with the board or owner and their accountants or attorneys. Our role is to coordinate, document and keep information current and accessible.',
        ]}
        points={['Draft annual budget preparation', 'Invoice and vendor contract tracking', 'Meeting agendas, notices and minutes support', 'Organized community records']}
        image={splitImages.admin}
        imageAlt="Community manager reviewing budget documents for a residential association"
      />

      <MaintenanceFeature />

      <Section labelledBy="process-heading">
        <Container>
          <SectionHeading
            id="process-heading"
            eyebrow="How it works"
            title="From first conversation to ongoing management."
          />
          <NumberedSteps steps={processSteps} />
        </Container>
      </Section>

      <ServicesClosing />
    </>
  );
}